import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Alert,
  alpha,
  useTheme,
  Divider,
  Stack,
  Avatar,
} from '@mui/material';
import {
  MoreVert,
  CheckCircle,
  Cancel,
  Visibility,
  TrendingUp,
  TrendingDown,
  Search,
} from '@mui/icons-material';
import { apiService } from '../services/api.service';
import { firebaseService } from '../services/firebase.service';
import { Transaction, Payment as PaymentType, WithdrawalRequest, User } from '../types';
import Layout from '../components/Layout';
import { getAvatarUrl } from '../utils/user';

const Financial: React.FC = () => {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [payments, setPayments] = useState<PaymentType[]>([]);
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [userTransactions, setUserTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleMenuClose = () => setAnchorEl(null);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<WithdrawalRequest | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [platformStats, setPlatformStats] = useState<{
    totalRevenue: number;
    platformFees: number;
    transactionFees: number;
    tutorEarnings: number;
    bookingCount: number;
  } | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<PaymentType | null>(null);
  const [paymentDetailsOpen, setPaymentDetailsOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [transactionDetailsOpen, setTransactionDetailsOpen] = useState(false);
  const [transactionUser, setTransactionUser] = useState<User | null>(null);
  const [loadingTransactionDetails, setLoadingTransactionDetails] = useState(false);

  const calculatePaymentBreakdown = (totalAmount: number) => {
    const classCharges = totalAmount / 1.13;
    const platformFee = classCharges * 0.10;
    const transactionFee = classCharges * 0.03;
    return {
      classCharges: parseFloat(classCharges.toFixed(2)),
      platformFee: parseFloat(platformFee.toFixed(2)),
      transactionFee: parseFloat(transactionFee.toFixed(2)),
      total: totalAmount,
    };
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = { page: page + 1, limit: rowsPerPage, search };

      if (tab === 0) {
        const result = await apiService.getTransactions(filters);
        setTransactions(result.data);
        setTotal(result.pagination.total);
      } else if (tab === 1) {
        const result = await apiService.getPayments(filters);
        setPayments(result.data);
        setTotal(result.pagination.total);
      } else if (tab === 2) {
        const result = await apiService.getWithdrawals(filters);
        setWithdrawals(result.data);
        setTotal(result.pagination.total);
      } else if (tab === 3) {
        if (selectedUser) {
          const result = await apiService.getTransactions({ ...filters, userId: selectedUser.id });
          setUserTransactions(result.data);
          setTotal(result.pagination.total);
        } else {
          setUserTransactions([]);
          setTotal(0);
        }
      } else if (tab === 4) {
        const bookings = await apiService.getBookings({ page: 1, limit: 10000, status: '' });
        const paidBookings = bookings.data.filter((b: any) => b.paymentStatus === 'paid');

        let totalRev = 0, totalPlat = 0, totalTx = 0, totalEarn = 0;
        paidBookings.forEach((b: any) => {
          const br = calculatePaymentBreakdown(b.amount);
          totalRev += br.total; totalPlat += br.platformFee; totalTx += br.transactionFee; totalEarn += br.classCharges;
        });

        setPlatformStats({
          totalRevenue: totalRev, platformFees: totalPlat, transactionFees: totalTx, tutorEarnings: totalEarn, bookingCount: paidBookings.length
        });
        setTotal(paidBookings.length);
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [tab, page, rowsPerPage, search, selectedUser]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadUsers = useCallback(async (q: string = '') => {
    setLoadingUsers(true);
    try {
      const res = await apiService.getUsers({ page: 1, limit: 100, search: q });
      setUsers(res.data);
    } catch (e) { console.error(e); } finally { setLoadingUsers(false); }
  }, []);

  useEffect(() => { if (tab === 3) loadUsers(); }, [tab, loadUsers]);

  useEffect(() => {
    if (tab === 3) {
      const id = setTimeout(() => loadUsers(userSearch), 300);
      return () => clearTimeout(id);
    }
  }, [userSearch, tab, loadUsers]);

  useEffect(() => {
    const loadTx = async () => {
      if (selectedTransaction && transactionDetailsOpen) {
        setLoadingTransactionDetails(true);
        try {
          if (selectedTransaction.userId) {
            const u = await apiService.getUser(selectedTransaction.userId);
            setTransactionUser(u);
          }
        } catch (e) { console.error(e); } finally { setLoadingTransactionDetails(false); }
      }
    };
    loadTx();
  }, [selectedTransaction, transactionDetailsOpen]);

  const handleWithdrawalMenuClick = (e: React.MouseEvent<HTMLElement>, w: WithdrawalRequest) => {
    setAnchorEl(e.currentTarget);
    setSelectedWithdrawal(w);
  };

  const handleApprove = async () => {
    if (selectedWithdrawal) {
      try {
        await apiService.approveWithdrawal(selectedWithdrawal.id);
        loadData();
        setApproveDialogOpen(false);
      } catch (e) { console.error(e); }
    }
    setAnchorEl(null);
  };

  const handleReject = async () => {
    if (selectedWithdrawal) {
      try {
        await apiService.rejectWithdrawal(selectedWithdrawal.id, rejectReason);
        loadData();
        setRejectDialogOpen(false);
        setRejectReason('');
      } catch (e) { console.error(e); }
    }
    setAnchorEl(null);
  };

  return (
    <Layout>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{
          mb: 4,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, letterSpacing: -1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>Financial Center</Typography>
            <Typography color="text.secondary" variant="body2">Monitor transactions, manage withdrawals, and analyze platform revenue.</Typography>
          </Box>
        </Box>

        <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: theme.shadows[2] }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : alpha(theme.palette.common.white, 0.02) }}>
            <Tabs
              value={tab}
              onChange={(e, v) => { setTab(v); setPage(0); if (v !== 3) setSelectedUser(null); }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 2, '& .MuiTab-root': { py: 2, fontWeight: 700, fontSize: '0.875rem' } }}>
              <Tab label="Transactions" />
              <Tab label="Payments" />
              <Tab label="Withdrawals" />
              <Tab label="Lookup" />
              <Tab label="Insights" />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress size={32} thickness={5} /></Box>
            ) : (
              <>
                <Box sx={{ mb: 3 }}>
                  {tab === 3 ? (
                    <Autocomplete
                      options={users}
                      getOptionLabel={(o) => `${o.studentProfile?.name || o.tutorProfile?.name || 'User'} (${o.email})`}
                      loading={loadingUsers}
                      value={selectedUser}
                      onChange={(e, v) => { setSelectedUser(v); setPage(0); }}
                      onInputChange={(e, v) => setUserSearch(v)}
                      renderInput={(params) => <TextField {...params} label="Search User Account" placeholder="Type name or email..." fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />}
                      renderOption={(props, o) => (
                        <Box component="li" {...props} key={o.id} sx={{ py: 1.5, px: 2 }}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                              src={getAvatarUrl(o)}
                              sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: 'primary.light' }}
                            >
                              {(o.studentProfile?.name || o.tutorProfile?.name || 'U').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box><Typography variant="body2" sx={{ fontWeight: 600 }}>{o.studentProfile?.name || o.tutorProfile?.name || 'User'}</Typography><Typography variant="caption" color="text.secondary">{o.email} • {o.role}</Typography></Box>
                          </Stack>
                        </Box>
                      )}
                    />
                  ) : tab !== 4 && (
                    <TextField
                      fullWidth
                      placeholder="Search records..."
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                      InputProps={{ startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} /> }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.action.hover, 0.5) } }}
                    />
                  )}
                </Box>

                {(tab === 0 || tab === 3) && (
                  <TableContainer>
                    <Table>
                      <TableHead><TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.04) }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Reference</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Flow</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Context</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Date</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Action</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {(tab === 0 ? transactions : userTransactions).map((tx) => (
                          <TableRow key={tx.id} hover>
                            <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.75rem', fontFamily: 'monospace' }}>#{tx.id.substring(0, 10)}</TableCell>
                            <TableCell>
                              <Chip
                                icon={tx.type === 'credit' ? <TrendingUp sx={{ fontSize: '1rem !important' }} /> : <TrendingDown sx={{ fontSize: '1rem !important' }} />}
                                label={tx.type}
                                size="small"
                                color={tx.type === 'credit' ? 'success' : 'error'}
                                sx={{
                                  fontWeight: 800,
                                  textTransform: 'uppercase',
                                  fontSize: '0.6rem',
                                  borderRadius: 1,
                                  bgcolor: alpha(theme.palette[tx.type === 'credit' ? 'success' : 'error'].main, 0.15),
                                  color: `${tx.type === 'credit' ? 'success' : 'error'}.main`,
                                  '& .MuiChip-icon': { color: 'inherit' }
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>₹{tx.amount}</TableCell>
                            <TableCell sx={{ maxWidth: 200 }}><Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>{tx.description}</Typography></TableCell>
                            <TableCell>
                              <Chip label={tx.status} size="small" sx={{ fontWeight: 700, fontSize: '0.625rem', borderRadius: 1, bgcolor: tx.status === 'completed' ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.warning.main, 0.1), color: tx.status === 'completed' ? 'success.main' : 'warning.main', height: 20 }} />
                            </TableCell>
                            <TableCell><Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{firebaseService.convertTimestamp(tx.createdAt).toLocaleDateString()}</Typography></TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => { setSelectedTransaction(tx); setTransactionDetailsOpen(true); }}><Visibility fontSize="small" /></IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {tab === 1 && (
                  <TableContainer>
                    <Table>
                      <TableHead><TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.04) }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Payment ID</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Method</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Purpose</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Date</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Action</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {payments.map((p) => (
                          <TableRow key={p.id} hover>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', fontFamily: 'monospace' }}>{p.id.substring(0, 12)}</TableCell>
                            <TableCell><Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{p.method}</Typography></TableCell>
                            <TableCell><Chip label={p.type} size="small" variant="outlined" sx={{ fontWeight: 700, fontSize: '0.6rem', height: 18 }} /></TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>₹{p.amount}</TableCell>
                            <TableCell><Chip label={p.status} color={p.status === 'success' ? 'success' : 'warning'} size="small" sx={{ fontWeight: 800, fontSize: '0.6rem', borderRadius: 1, bgcolor: alpha(theme.palette[p.status === 'success' ? 'success' : 'warning'].main, 0.15), color: `${p.status === 'success' ? 'success' : 'warning'}.main` }} /></TableCell>
                            <TableCell><Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{firebaseService.convertTimestamp(p.createdAt).toLocaleDateString()}</Typography></TableCell>
                            <TableCell align="right"><IconButton size="small" onClick={() => { setSelectedPayment(p); setPaymentDetailsOpen(true); }}><MoreVert fontSize="small" /></IconButton></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {tab === 2 && (
                  <TableContainer>
                    <Table>
                      <TableHead><TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.04) }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Request ID</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Tutor</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Requested At</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Action</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {withdrawals.map((w) => (
                          <TableRow key={w.id} hover>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>#{w.id.substring(0, 8)}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{w.tutorId}</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>₹{w.amount}</TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  w.status === 'success' ? 'completed' :
                                    w.status === 'released' ? 'initiated' :
                                      w.status === 'inProgress' ? 'in-progress' :
                                        w.status === 'requested' ? 'requested' :
                                          w.status
                                }
                                color={
                                  w.status === 'success' ? 'success' :
                                    w.status === 'rejected' ? 'error' :
                                      w.status === 'requested' ? 'info' :
                                        'warning'
                                }
                                size="small"
                                sx={{ fontWeight: 800, fontSize: '0.6rem', borderRadius: 1, textTransform: 'uppercase' }}
                              />
                            </TableCell>
                            <TableCell><Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{firebaseService.convertTimestamp(w.requestedAt).toLocaleDateString()}</Typography></TableCell>
                            <TableCell align="right">
                              {w.status === 'pending' && <IconButton size="small" onClick={(e) => handleWithdrawalMenuClick(e, w)}><MoreVert fontSize="small" /></IconButton>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {tab === 4 && platformStats && (
                  <Box sx={{ p: 1 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
                      <Paper sx={{ p: 2.5, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.1) }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main', textTransform: 'uppercase', letterSpacing: 1 }}>Gross Revenue</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>₹{platformStats.totalRevenue.toLocaleString()}</Typography>
                      </Paper>
                      <Paper sx={{ p: 2.5, borderRadius: 3, bgcolor: alpha(theme.palette.success.main, 0.05), border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.1) }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'success.main', textTransform: 'uppercase', letterSpacing: 1 }}>Platform Commission</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>₹{platformStats.platformFees.toLocaleString()}</Typography>
                      </Paper>
                      <Paper sx={{ p: 2.5, borderRadius: 3, bgcolor: alpha(theme.palette.info.main, 0.05), border: '1px solid', borderColor: alpha(theme.palette.info.main, 0.1) }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'info.main', textTransform: 'uppercase', letterSpacing: 1 }}>Gateway Charges</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>₹{platformStats.transactionFees.toLocaleString()}</Typography>
                      </Paper>
                      <Paper sx={{ p: 2.5, borderRadius: 3, bgcolor: alpha(theme.palette.warning.main, 0.05), border: '1px solid', borderColor: alpha(theme.palette.warning.main, 0.1) }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'warning.main', textTransform: 'uppercase', letterSpacing: 1 }}>Tutor Payouts</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>₹{platformStats.tutorEarnings.toLocaleString()}</Typography>
                      </Paper>
                    </Box>

                    <Alert severity="info" variant="outlined" sx={{ borderRadius: 3, '& .MuiAlert-message': { width: '100%' } }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Fee Calculation Policy</Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Platform Fees are calculated as 10% of class charges. Gateway Transaction Fees are fixed at 3%. Total amount paid by students is inclusive of these fees.
                        <br /><strong>Class Charges = (Total / 1.13)</strong>. All values shown are derived from finalized paid bookings.
                      </Typography>
                    </Alert>
                  </Box>
                )}

                {tab !== 4 && (
                  <TablePagination component="div" count={total} page={page} onPageChange={(e, n) => setPage(n)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} sx={{ borderTop: 1, borderColor: 'divider' }} />
                )}
              </>
            )}
          </Box>
        </Paper>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={{ sx: { borderRadius: 2, minWidth: 160 } }}>
          <MenuItem onClick={() => setApproveDialogOpen(true)} sx={{ gap: 1.5, py: 1.25, color: 'success.main' }}><CheckCircle fontSize="small" /><Typography variant="body2" fontWeight={600}>Approve Payout</Typography></MenuItem>
          <MenuItem onClick={() => setRejectDialogOpen(true)} sx={{ gap: 1.5, py: 1.25, color: 'error.main' }}><Cancel fontSize="small" /><Typography variant="body2" fontWeight={600}>Reject Payout</Typography></MenuItem>
        </Menu>

        {/* Dialogs and Details remain functional as before but with polished styling */}
        <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 800 }}>Confirm Approval</DialogTitle>
          <DialogContent><Typography variant="body2">Are you sure you want to approve the withdrawal request of <strong>₹{selectedWithdrawal?.amount}</strong>? This action will mark the request as paid and update the tutor's balance.</Typography></DialogContent>
          <DialogActions sx={{ p: 3 }}><Button onClick={() => setApproveDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancel</Button><Button onClick={handleApprove} variant="contained" sx={{ fontWeight: 700, borderRadius: 2 }}>Confirm Approval</Button></DialogActions>
        </Dialog>

        <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 800 }}>Reject Withdrawal</DialogTitle>
          <DialogContent><TextField fullWidth label="Reason for Rejection" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} margin="normal" multiline rows={3} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} placeholder="e.g., Bank details incorrect..." /></DialogContent>
          <DialogActions sx={{ p: 3 }}><Button onClick={() => setRejectDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancel</Button><Button onClick={handleReject} variant="contained" color="error" disabled={!rejectReason} sx={{ fontWeight: 700, borderRadius: 2 }}>Reject Request</Button></DialogActions>
        </Dialog>

        <Dialog open={transactionDetailsOpen} onClose={() => setTransactionDetailsOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>Transaction Audit</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>#{selectedTransaction?.id}</Typography>

            {loadingTransactionDetails ? (
              <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
            ) : (
              <Stack spacing={3}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem' }}>Flow</Typography><Chip label={selectedTransaction?.type} color={selectedTransaction?.type === 'credit' ? 'success' : 'error'} size="small" sx={{ fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem', mt: 0.5, borderRadius: 1, bgcolor: alpha(theme.palette[selectedTransaction?.type === 'credit' ? 'success' : 'error'].main, 0.1) }} /></Box>
                  <Box><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem' }}>Amount</Typography><Typography variant="h6" sx={{ fontWeight: 800 }}>₹{selectedTransaction?.amount}</Typography></Box>
                </Box>
                <Box><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem' }}>Description</Typography><Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedTransaction?.description}</Typography></Box>
                <Divider />
                <Box><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem' }}>Account Owner</Typography>
                  {transactionUser ? (
                    <Stack direction="row" spacing={1.5} sx={{ mt: 1 }} alignItems="center">
                      <Avatar
                        src={getAvatarUrl(transactionUser)}
                        sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}
                      >
                        {(transactionUser.studentProfile?.name || transactionUser.tutorProfile?.name || 'U').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box><Typography variant="body2" sx={{ fontWeight: 700 }}>{transactionUser.studentProfile?.name || transactionUser.tutorProfile?.name || 'User'}</Typography><Typography variant="caption" color="text.secondary">{transactionUser.email}</Typography></Box>
                    </Stack>
                  ) : <Typography variant="body2" color="text.disabled">User not found</Typography>}
                </Box>
              </Stack>
            )}
            <Button onClick={() => setTransactionDetailsOpen(false)} variant="contained" fullWidth sx={{ mt: 4, py: 1.5, borderRadius: 3, fontWeight: 700 }}>Close Record</Button>
          </Box>
        </Dialog>

        <Dialog open={paymentDetailsOpen} onClose={() => setPaymentDetailsOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>Payment Voucher</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>#{selectedPayment?.id.substring(0, 15)}...</Typography>
            <Stack spacing={3}>
              <Box sx={{ p: 2, borderRadius: 3, bgcolor: alpha(theme.palette.success.main, 0.05), border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.1) }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'success.main', textTransform: 'uppercase' }}>Confirmed Total</Typography>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>₹{selectedPayment?.amount}</Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem' }}>Method</Typography><Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>{selectedPayment?.method}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem' }}>Type</Typography><Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>{selectedPayment?.type}</Typography></Box>
              </Box>
              <Box><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem' }}>Reference ID</Typography><Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>{selectedPayment?.razorpayPaymentId || (selectedPayment as any)?.walletPaymentId || 'N/A'}</Typography></Box>
            </Stack>
            <Button onClick={() => setPaymentDetailsOpen(false)} variant="contained" fullWidth sx={{ mt: 4, py: 1.5, borderRadius: 3, fontWeight: 700 }}>Close Record</Button>
          </Box>
        </Dialog>
      </Box >
    </Layout >
  );
};

export default Financial;
